import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DatePicker } from '@/components/ui/date-picker';
import { useAddInvoicePayment } from '@/hooks/useInvoices';
import type { CustomerInvoice, PaymentMethod } from '@/apis/invoices';
import {
  FieldError,
  InvoiceSelect,
  PAYMENT_METHOD_OPTIONS,
  formatMoney,
  getInvoiceBalance,
} from './invoice-shared';

const schema = z.object({
  method: z.string().min(1, 'Select how it was paid'),
  amount: z
    .string()
    .min(1, 'Enter an amount')
    .refine((v) => !isNaN(Number(v)) && Number(v) !== 0, 'Enter a valid amount'),
  paidAt: z.string().min(1, 'Required'),
  reference: z.string().optional(),
  note: z.string().optional(),
});

type Values = z.infer<typeof schema>;

const fieldClass =
  'h-10 w-full rounded-xl border border-smoke px-3 text-base text-black outline-none focus:border-primary-s500';
const labelClass = 'mb-1.5 block text-sm font-medium text-black';

export function RecordPaymentDialog({
  invoice,
  onClose,
}: {
  invoice: CustomerInvoice | null;
  onClose: () => void;
}) {
  const addPayment = useAddInvoicePayment();
  const balance = invoice ? getInvoiceBalance(invoice) : 0;

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Values>({
    resolver: zodResolver(schema),
    values: {
      method: 'check',
      amount: balance ? String(balance) : '',
      paidAt: new Date().toISOString().slice(0, 10),
      reference: '',
      note: '',
    },
  });

  const onSubmit = handleSubmit((values) => {
    if (!invoice) return;
    addPayment.mutate(
      {
        id: invoice.id,
        payload: {
          method: values.method as PaymentMethod,
          amount: Number(values.amount),
          paidAt: values.paidAt,
          reference: values.reference || undefined,
          note: values.note || undefined,
        },
      },
      { onSuccess: onClose },
    );
  });

  return (
    <Dialog
      open={!!invoice}
      onOpenChange={(o) => !o && !addPayment.isPending && onClose()}
    >
      <DialogContent className="w-[520px] max-w-[90vw] p-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Mark as Paid
          </DialogTitle>
        </DialogHeader>

        <p className="mb-4 text-sm text-lightGray">
          Balance due:{' '}
          <span className="font-medium text-black">{formatMoney(balance)}</span>
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>How it was paid</label>
            <Controller
              control={control}
              name="method"
              render={({ field }) => (
                <InvoiceSelect
                  value={field.value}
                  onChange={field.onChange}
                  options={PAYMENT_METHOD_OPTIONS}
                  className="h-10 rounded-xl px-3 py-0 text-base"
                />
              )}
            />
            <FieldError message={errors.method?.message} />
          </div>
          <div>
            <label className={labelClass}>Amount</label>
            <input
              {...register('amount')}
              placeholder="0.00"
              className={fieldClass}
            />
            <FieldError message={errors.amount?.message} />
          </div>
          <div>
            <label className={labelClass}>Payment Date</label>
            <Controller
              control={control}
              name="paidAt"
              render={({ field }) => (
                <DatePicker
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                />
              )}
            />
            <FieldError message={errors.paidAt?.message} />
          </div>
          <div>
            <label className={labelClass}>Reference</label>
            <input
              {...register('reference')}
              placeholder="Check #, last 4, wire ref"
              className={fieldClass}
            />
          </div>
          <div className="col-span-2">
            <label className={labelClass}>Note</label>
            <input
              {...register('note')}
              placeholder="Optional"
              className={fieldClass}
            />
          </div>
        </div>

        <div className="mt-6 flex gap-4">
          <button
            type="button"
            onClick={onClose}
            disabled={addPayment.isPending}
            className="flex-1 rounded-full bg-primary-s50 py-3 text-base font-medium text-primary-s500 hover:opacity-80 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void onSubmit()}
            disabled={addPayment.isPending}
            className="flex-1 rounded-full bg-primary-s500 py-3 text-base font-medium text-neutral-white hover:bg-blue-700 disabled:opacity-50"
          >
            {addPayment.isPending ? 'Saving…' : 'Mark as Paid'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
